import { produce } from 'immer';
import type { ClipboardEvent, FormEvent, KeyboardEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { border, primaryBlue } from '../models/colors';

const KEY_CODE = {
	backspace: 8,
	left: 37,
	up: 38,
	right: 39,
	down: 40,
};

interface IStyledProps {
	value: string;
	index: number;
	width?: number;
	height?: number;
}

interface IVerificationFieldProps {
	type?: ValidType;
	length?: number;
	autoFocus?: boolean;
	inputElements?: InputElements;
	onKeyDown?: KeyEvent;
	onChange?: ChangeEvent;
	onPaste?: PasteEvent;
	checkValidity?: (v: string) => void;
}

type ValidType = 'numeric' | 'alphabetical' | 'alphanumeric' | 'any';
type FieldValues = string[];
type InputElements = Array<HTMLInputElement | null>;
type KeyType = KeyboardEvent<HTMLInputElement>;
type KeyEvent = (e: KeyType) => void;
type ChangeType = FormEvent<HTMLInputElement>;
type ChangeEvent = (e: ChangeType) => void;
type PasteType = ClipboardEvent<HTMLInputElement>;
type PasteEvent = (e: PasteType) => void;

const Container = styled('div')`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-wrap: nowrap;
`;

const Input = styled(
	({
		length,
		autoFocus,
		onChange,
		value,
		index,
		inputElements,
		onPaste,
		...rest
	}: IVerificationFieldProps & IStyledProps) => {
		const inputRef = useRef<HTMLInputElement>(null);

		useEffect(() => {
			inputElements![index] = inputRef.current;
		}, [inputElements, index]);
		return (
			<input
				type="text"
				name={`field-${index}`}
				value={value || ''}
				data-id={`${index}`}
				onChange={onChange}
				onPaste={e => {
					e.preventDefault();
					if (onPaste) {
						onPaste(e);
					}
				}}
				ref={inputRef}
				{...rest}
			/>
		);
	}
)`
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans',
		'Droid Sans', 'Helvetica Neue', sans-serif;
	font-weight: bold;
	font-size: 1.5rem;
	text-align: center;
	box-sizing: border-box;
	width: 48px;
	height: 48px;
	border: solid 2px ${border};
	border-radius: 4px;

	&:focus {
		outline: none;
		border: solid 2px ${primaryBlue};
		caret-color: ${primaryBlue};
	}

	& + & {
		margin-left: 4px;
	}
`;

if (!Array.prototype.fill) {
	Object.defineProperty(Array.prototype, 'fill', {
		value: function (value: any, start?: number, end?: number) {
			if (this === undefined) {
				throw new TypeError('this is null or not defined');
			}

			const item = Object(this);
			const length = item.length >>> 0;
			const relativeStart = (start ?? 0) >> 0;
			let range = relativeStart < 0 ? Math.max(length + relativeStart, 0) : Math.min(relativeStart, length);
			const relativeEnd = (end === undefined ? length : end) >> 0;
			const final = relativeEnd < 0 ? Math.max(length + relativeEnd, 0) : Math.min(relativeEnd, length);

			while (range < final) {
				item[range] = value;
				range++;
			}

			return item;
		},
	});
}

export const VerificationCode = (props: IVerificationFieldProps) => {
	const { type = 'numeric', length = 6, autoFocus = true } = props;
	const [values, setValues] = useState<FieldValues>(Array(length).fill(''));
	const inputElementsRef = useRef<InputElements>(Array(length).fill(null));

	const getIndexes = (currentIndex: number, numberOfElements: number) => {
		const firstElement = currentIndex === 0;
		const lastElement = currentIndex === numberOfElements - 1;
		const prevIndex = firstElement ? 0 : currentIndex - 1;
		const nextIndex = lastElement ? 0 : currentIndex + 1;
		return { firstElement, lastElement, prevIndex, nextIndex };
	};

	const getRegExp = (t: ValidType) =>
		t === 'numeric' ? /\d+/g : t === 'alphabetical' ? /[A-Za-z]+/g : t === 'alphanumeric' ? /[A-Za-z\d]+/g : /.+/g;

	const handleNavigation = (currentIndex: number, elements: InputElements, e: KeyType) => {
		const { firstElement, lastElement, prevIndex, nextIndex } = getIndexes(currentIndex, elements.length);

		switch (e.keyCode) {
			case KEY_CODE.backspace: {
				e.preventDefault();
				const nextState = produce(values, draftState => {
					draftState[currentIndex] = '';
					if (!firstElement) {
						if (elements[prevIndex]!.value.length === 1 && elements[currentIndex]!.value.length === 0) {
							draftState[prevIndex] = '';
						}
						elements[prevIndex]!.select();
					}
				});
				setValues(nextState);
				break;
			}
			case KEY_CODE.left:
				if (!firstElement) {
					elements[prevIndex]!.select();
				}
				break;
			case KEY_CODE.right:
				if (!lastElement) {
					elements[nextIndex]!.select();
				}
				break;
			case KEY_CODE.up:
			case KEY_CODE.down:
				e.preventDefault();
				break;
			default:
				break;
		}
	};

	const validChar = (v: string, t: ValidType) =>
		v.match(getRegExp(t)) !== null ? v.match(getRegExp(t))!.join() : '';

	const onChange = (index: number, prevState: FieldValues, elements: InputElements, e: ChangeType, t: ValidType) => {
		let isValid = true;
		const value = e.currentTarget.value.toUpperCase();

		const nextState = produce(prevState, draftState => {
			if (value.length === 1) {
				const newChar = validChar(value, t);
				if (!newChar) {
					isValid = false;
				} else {
					draftState[index] = newChar;
				}
			} else {
				const newChar = validChar(value.replace(prevState[index], ''), t);
				if (!newChar) {
					draftState[index] = prevState[index];
					isValid = false;
				} else {
					draftState[index] = newChar;
				}
			}
		});
		setValues(nextState);

		const { lastElement, nextIndex } = getIndexes(index, elements.length);
		if (!lastElement && isValid) {
			elements[nextIndex]!.select();
		}
	};

	const onPaste = (prevState: FieldValues, i: InputElements, e: PasteType, t: ValidType) => {
		const pasted = e.clipboardData
			.getData('Text')
			.split('')
			.map(v => {
				return validChar(v, t);
			})
			.filter(v => v.length !== 0);
		setValues(produce(prevState, draftState => draftState.map((_, idx) => pasted[idx])));
		i[pasted.length < i.length ? pasted.length : i.length - 1]!.select();
	};

	useEffect(() => {
		if (autoFocus) {
			inputElementsRef.current[0]?.select();
		}
	}, [autoFocus]);

	useEffect(() => {
		if (props.checkValidity) {
			props.checkValidity(values.join(''));
		}
	}, [props, values]);

	return (
		<Container>
			{values.map((value, index) => (
				<Input
					length={length}
					autoFocus={autoFocus}
					value={value}
					key={index}
					index={index}
					inputElements={inputElementsRef.current}
					onKeyDown={(e: KeyType) => handleNavigation(index, inputElementsRef.current, e)}
					onChange={(e: ChangeType) => onChange(index, values, inputElementsRef.current, e, type)}
					onPaste={(e: PasteType) => onPaste(values, inputElementsRef.current, e, type)}
				/>
			))}
		</Container>
	);
};
